import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSpring, animated } from 'react-spring';

/**
 * Node data for the mini-map visualization
 */
interface MiniMapNode {
  /** Unique identifier for the node */
  id: string;
  /** X position in the main tree view (pixels) */
  x: number;
  /** Y position in the main tree view (pixels) */
  y: number;
  /** Node width (pixels) */
  width: number;
  /** Node height (pixels) */
  height: number;
  /** Current status of the task */
  status: 'done' | 'blocked' | 'pending' | 'archived';
  /** Priority level of the task */
  priority: 'low' | 'medium' | 'high' | 'critical';
}

/**
 * Props for the MiniMap component
 */
interface MiniMapProps {
  /** Ref to the main tree container for tracking scroll/viewport */
  treeContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Current zoom level of the main tree view */
  zoom: number;
  /** Callback to navigate to a specific position in the main tree */
  onNavigate: (x: number, y: number) => void;
  /** Array of nodes to display in the mini-map */
  nodes: MiniMapNode[];
}

/**
 * MiniMap - Overview navigation component for large tree visualizations
 *
 * Provides a bird's-eye view of the entire tree structure with:
 * - Scaled-down representation of all visible nodes
 * - Color-coded nodes based on status (done/blocked/pending/archived) and priority
 * - Viewport indicator showing current visible area
 * - Click/drag navigation to quickly jump to any part of the tree
 *
 * The mini-map only appears when there are more than 5 nodes in the tree,
 * as smaller trees don't benefit from the overview navigation.
 *
 * @example
 * ```tsx
 * <MiniMap
 *   treeContainerRef={scrollContainerRef}
 *   zoom={currentZoom}
 *   onNavigate={(x, y) => scrollTo(x, y)}
 *   nodes={miniMapNodes}
 * />
 * ```
 */
const MiniMap: React.FC<MiniMapProps> = ({
  treeContainerRef,
  zoom,
  onNavigate,
  nodes
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [viewport, setViewport] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [treeSize, setTreeSize] = useState({ width: 0, height: 0 });
  const [treeBounds, setTreeBounds] = useState({ minX: 0, minY: 0 });
  const miniMapRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate scale factor for mini-map.
  // Guard against the initial 0x0 treeSize, which would make the ratios Infinity.
  const MINIMAP_SIZE = 200;
  const scale = Math.min(
    MINIMAP_SIZE / (treeSize.width || MINIMAP_SIZE),
    MINIMAP_SIZE / (treeSize.height || MINIMAP_SIZE),
    0.15
  );

  // Update viewport and tree size
  useEffect(() => {
    const updateDimensions = () => {
      const container = treeContainerRef.current;
      if (!container) return;

      // Calculate tree bounds
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      nodes.forEach(node => {
        minX = Math.min(minX, node.x);
        minY = Math.min(minY, node.y);
        maxX = Math.max(maxX, node.x + node.width);
        maxY = Math.max(maxY, node.y + node.height);
      });

      if (nodes.length > 0) {
        const padding = 50;
        setTreeSize({
          width: maxX - minX + padding * 2,
          height: maxY - minY + padding * 2
        });
        setTreeBounds({ minX, minY });
      }

      // Update viewport
      setViewport({
        x: container.scrollLeft,
        y: container.scrollTop,
        width: container.clientWidth,
        height: container.clientHeight
      });
    };

    updateDimensions();
    const container = treeContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateDimensions);
      window.addEventListener('resize', updateDimensions);
      return () => {
        container.removeEventListener('scroll', updateDimensions);
        window.removeEventListener('resize', updateDimensions);
      };
    }
  }, [treeContainerRef, nodes, zoom]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    const rect = contentRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = (e.clientX - rect.left) / scale + treeBounds.minX - viewport.width / 2;
    const y = (e.clientY - rect.top) / scale + treeBounds.minY - viewport.height / 2;

    onNavigate(Math.max(0, x), Math.max(0, y));
  }, [scale, viewport.width, viewport.height, onNavigate, treeBounds]);

  // While dragging, track movement on the document so the drag keeps working
  // even when the cursor leaves the mini-map (mirrors usePanning).
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = contentRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = (e.clientX - rect.left) / scale + treeBounds.minX - viewport.width / 2;
      const y = (e.clientY - rect.top) / scale + treeBounds.minY - viewport.height / 2;

      onNavigate(Math.max(0, x), Math.max(0, y));
    };

    const handleMouseUp = () => setIsDragging(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, scale, viewport.width, viewport.height, onNavigate, treeBounds]);

  // Animation for mini-map appearance
  const springProps = useSpring({
    opacity: nodes.length > 5 ? 1 : 0,
    transform: nodes.length > 5 ? 'scale(1)' : 'scale(0.9)',
    config: { tension: 300, friction: 30 }
  });

  // Status color mapping. Returns CSS variable references so the mini-map nodes
  // (rendered as DOM elements with inline backgroundColor) re-resolve their
  // colors automatically when the theme attribute flips — no repaint required.
  const getStatusColor = (status: string, priority: string) => {
    if (status === 'done') return 'var(--state-done)';
    if (status === 'blocked') return 'var(--state-blocked)';
    if (status === 'archived') return 'var(--state-archived)';

    // Priority colors for pending items
    switch (priority) {
      case 'critical': return 'var(--priority-critical)';
      case 'high': return 'var(--priority-high)';
      case 'medium': return 'var(--priority-medium)';
      case 'low':
      default: return 'var(--text-tertiary)'; // neutral grey — distinct from the 'done' green
    }
  };

  if (nodes.length <= 5) return null;

  return (
    <animated.div
      style={springProps}
      className="mini-map"
      ref={miniMapRef}
      onMouseDown={handleMouseDown}
    >
      <div className="mini-map-header">
        <span className="mini-map-title">Overview</span>
      </div>

      <div
        ref={contentRef}
        className="mini-map-content"
        style={{
          width: treeSize.width * scale,
          height: treeSize.height * scale,
          maxWidth: MINIMAP_SIZE,
          maxHeight: MINIMAP_SIZE
        }}
      >
        {/* Render nodes */}
        {nodes.map(node => (
          <div
            key={node.id}
            className="mini-map-node"
            style={{
              left: (node.x - treeBounds.minX) * scale,
              top: (node.y - treeBounds.minY) * scale,
              width: Math.max(2, node.width * scale),
              height: Math.max(2, node.height * scale),
              backgroundColor: getStatusColor(node.status, node.priority)
            }}
          />
        ))}

        {/* Viewport indicator */}
        {(() => {
          const contentWidth = Math.min(treeSize.width * scale, MINIMAP_SIZE);
          const contentHeight = Math.min(treeSize.height * scale, MINIMAP_SIZE);
          const clampedLeft = Math.max(0, (viewport.x - treeBounds.minX) * scale);
          const clampedTop = Math.max(0, (viewport.y - treeBounds.minY) * scale);
          const clampedWidth = Math.min(viewport.width * scale, contentWidth - clampedLeft);
          const clampedHeight = Math.min(viewport.height * scale, contentHeight - clampedTop);
          return (
            <div
              className="mini-map-viewport"
              style={{
                left: clampedLeft,
                top: clampedTop,
                width: Math.max(0, clampedWidth),
                height: Math.max(0, clampedHeight),
              }}
            />
          );
        })()}
      </div>
    </animated.div>
  );
};

export default MiniMap;